import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { InputmaskOptions } from '@ngneat/input-mask';
import { CompanyOfficeForm } from 'src/app/classes/form-groups/company-office-form';
import {
  Company,
  CompanyOffice,
  SubNationalDivision
} from 'src/app/classes/models';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-add-edit-service-region-modal',
  templateUrl: './add-edit-service-region-modal.component.html',
  styleUrl: './add-edit-service-region-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditServiceRegionModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {}

  @Input() CompanyOffice?: CompanyOffice;
  @Input() Company?: Company;

  CompanyOfficeForm: CompanyOfficeForm = new CompanyOfficeForm();
  LocalizationInfo!: LocalizationInfo;
  SubNationalDivisions: SubNationalDivision[] = [];
  zipMask?: InputmaskOptions<any>;

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  save() {
    if (!this.CompanyOffice) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot Save. No office record found.',
        { returnError: true }
      );
    }

    if (!this.CompanyOfficeForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot Save. No office form found.',
        { returnError: true }
      );
    }

    const errors = this.UtilityService.generateValidationErrorsObject(
      this.CompanyOfficeForm
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot Save. There are errors in the form.',
        { returnError: true }
      );
    }

    Object.assign(this.CompanyOffice, this.CompanyOfficeForm.getRawValue());

    this.activeModal.close();
  }

  ngOnInit(): void {
    if (!this.CompanyOffice) {
      throw this.ToastService.addErrorToast('Error', 'No office found.', {
        returnError: true
      });
    }

    this.CompanyOfficeForm.patchValue(this.CompanyOffice);

    const StreetAddressForm = this.CompanyOfficeForm.controls.StreetAddress;

    const requiredStreetAddressFields = ['address1', 'city', 'state', 'zip'];

    requiredStreetAddressFields.forEach((controlName) => {
      const control = StreetAddressForm.get(controlName);

      if (control) {
        control.addValidators([Validators.required]);
      }
    });

    if (!this.Company) {
      throw this.ToastService.addErrorToast('Error', 'No Company info found.', {
        returnError: true
      });
    }

    const { country } = this.Company;

    if (!country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Company country found.',
        { returnError: true }
      );
    }

    this.LocalizationInfo = this.UtilityService.getLocalizedValuesMap(country);

    this.zipMask =
      country === 'CA'
        ? this.UtilityService.canadianZipMask
        : this.UtilityService.usZipMask;

    this.SubNationalDivisionService.getSubNationalDivisions({
      country: country === 'CA' ? 'Canada' : 'USA'
    }).subscribe({
      next: (data: SubNationalDivision[]) => {
        if (!data) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No SuNationalDivisions found.',
            { returnError: true }
          );
        }

        this.SubNationalDivisions = data;
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }
}
